import React, { useEffect, useState } from 'react';
import { api, formatCOP } from '../services/api';
import useInvalidate from '../hooks/useInvalidate';
import { getAppUrls } from '../utils/appUrls';
import { pedidoClienteLabel } from '../utils/textEncoding';

function fmtDuracion(inicio) {
  if (!inicio) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(inicio).getTime()) / 60000));
  if (mins < 1) return 'hace segundos';
  if (mins === 1) return 'hace 1 min';
  if (mins < 60) return `hace ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `hace ${h}h ${m}m` : `hace ${h}h`;
}

/** Solo-GPS: fuera de lista si última señal > 5 min. */
const GPS_CAMPO_MS = 5 * 60 * 1000;
const GPS_FRESH_MS = 2 * 60 * 1000;

function isEnCampoVisible(v) {
  if (!v) return false;
  if (!v.live_only && !String(v.id || '').startsWith('live-')) return true;
  const at = v.vendedor_ubicacion_at || v.inicio;
  if (!at) return false;
  const age = Date.now() - new Date(at).getTime();
  return Number.isFinite(age) && age <= GPS_CAMPO_MS;
}

function campoAgeLabel(v) {
  const at = v.vendedor_ubicacion_at || v.inicio;
  if (!at) return '—';
  const age = Date.now() - new Date(at).getTime();
  if (!Number.isFinite(age)) return '—';
  if (age < GPS_FRESH_MS) return 'Señal reciente';
  return fmtDuracion(at);
}

function mapsUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function BarChart({ data }) {
  if (!data?.length) {
    return <p className="muted">Sin pedidos esta semana</p>;
  }

  const max = Math.max(...data.map((d) => d.pedidos), 1);

  return (
    <div className="bar-chart">
      {data.map((d) => {
        const pct = Math.round((d.pedidos / max) * 100);
        const label = new Date(d.dia).toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric' });
        return (
          <div key={d.dia} className="bar-col">
            <div className="bar-track">
              <div className="bar-fill" style={{ height: `${Math.max(pct, 8)}%` }} title={`${d.pedidos} pedidos`} />
            </div>
            <span className="bar-val">{d.pedidos}</span>
            <span className="bar-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

const ESTADO = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_preparacion: 'En prep.',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

export default function DashboardPage({ pin, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const apps = getAppUrls();

  function load(silent = false) {
    if (!silent) setLoading(true);
    api
      .getAdminDashboard()
      .then((r) => {
        setData(r.data);
        setError('');
      })
      .catch((err) => {
        if (!silent) setData(null);
        setError(err.message || 'No se pudo cargar el resumen');
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), 45000);
    return () => clearInterval(t);
  }, [pin]);

  useInvalidate(null, () => load(true));

  if (loading && !data) {
    return <p className="muted">Cargando resumen...</p>;
  }

  if (error && !data) {
    return (
      <div className="panel">
        <p className="error banner">{error}</p>
        <button type="button" className="btn-secondary" onClick={() => load(false)}>
          Reintentar
        </button>
      </div>
    );
  }

  if (!data) return null;

  const enCampoList = (data.vendedores_en_campo || []).filter(isEnCampoVisible);
  const enCampoCount = enCampoList.length;

  return (
    <div className="dashboard">
      {error ? (
        <p className="error banner" style={{ marginBottom: 12 }}>
          {error} — mostrando último resumen cargado.
        </p>
      ) : null}
      <header className="dashboard-hero">
        <div>
          <p className="dashboard-kicker">Panel OH Belleza</p>
          <h1>Resumen del día</h1>
          <p>Ventas, pedidos y vendedores en campo</p>
        </div>
        <div className="dashboard-hero-date">
          {new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
      </header>

      <div className="stat-grid">
        <div className="stat-card stat-pink">
          <span>Ventas hoy</span>
          <strong>{formatCOP(data.ventas_hoy)}</strong>
        </div>
        <div className="stat-card stat-purple">
          <span>Pedidos hoy</span>
          <strong>{data.pedidos_hoy}</strong>
        </div>
        <div className="stat-card stat-orange">
          <span>Pendientes</span>
          <strong>{data.pedidos_pendientes}</strong>
        </div>
        <div className="stat-card stat-green">
          <span>En campo (señal ≤5 min)</span>
          <strong>{enCampoCount}</strong>
        </div>
        <div className="stat-card">
          <span>Clientes</span>
          <strong>{data.clientes}</strong>
        </div>
        <div className="stat-card">
          <span>Productos activos</span>
          <strong>{data.productos}</strong>
        </div>
      </div>

      <div className="dashboard-grid">
        <section className="dash-panel">
          <header className="dash-panel-head">
            <h2>Pedidos — últimos 7 días</h2>
          </header>
          <BarChart data={data.pedidos_semana} />
        </section>

        <section className="dash-panel">
          <header className="dash-panel-head">
            <h2>Vendedores en campo</h2>
            {enCampoCount > 0 ? (
              <button type="button" className="link-btn" onClick={() => onNavigate?.('visitas')}>
                Ver mapa →
              </button>
            ) : null}
          </header>
          {!enCampoList.length ? (
            <p className="muted">Nadie con señal reciente</p>
          ) : (
            <ul className="campo-list">
              {enCampoList.map((v) => (
                <li key={v.id}>
                  <div>
                    <strong>{v.vendedor_nombre}</strong>
                    <span>
                      {v.live_only || String(v.id).startsWith('live-')
                        ? 'Última señal GPS'
                        : `${v.sucursal_nombre || 'Visita'}${v.ciudad ? ` · ${v.ciudad}` : ''}`}
                    </span>
                    {v.lat != null && v.lng != null ? (
                      <a
                        className="campo-map-link"
                        href={mapsUrl(v.lat, v.lng)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Ver en mapa
                      </a>
                    ) : null}
                  </div>
                  <em>{campoAgeLabel(v)}</em>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="dash-panel">
        <header className="dash-panel-head">
          <h2>Últimos pedidos</h2>
          <button type="button" className="link-btn" onClick={() => onNavigate?.('pedidos')}>
            Ver historial →
          </button>
        </header>
        <div className="table-wrap">
          <table className="visitas-table">
            <thead>
              <tr>
                <th>Pedido</th>
                <th>Cliente</th>
                <th>Vendedor</th>
                <th>Estado</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.ultimos_pedidos || []).map((p) => (
                <tr key={p.id}>
                  <td className="mono" data-label="Pedido">{p.numero}</td>
                  <td data-label="Cliente">
                    {pedidoClienteLabel(p)}
                    {p.es_sede || p.padre_codigo ? (
                      <span className="cliente-sede-pill"> Sucursal</span>
                    ) : null}
                  </td>
                  <td data-label="Vendedor">{p.vendedor || '—'}</td>
                  <td data-label="Estado"><span className={`estado-pill estado-${p.estado}`}>{ESTADO[p.estado] || p.estado}</span></td>
                  <td data-label="Total">{formatCOP(p.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="dash-panel dash-apps">
        <header className="dash-panel-head">
          <h2>Apps del sistema</h2>
        </header>
        <ul className="apps-list">
          <li>
            <strong>Vendedor</strong> —{' '}
            <a href={apps.vendedor.href} target="_blank" rel="noreferrer">
              {apps.vendedor.label}
            </a>
          </li>
          <li>
            <strong>Admin</strong> —{' '}
            <a href={apps.admin.href} target="_blank" rel="noreferrer">
              {apps.admin.label}
            </a>
          </li>
          <li>
            <strong>Oficina</strong> —{' '}
            <a href={apps.oficina.href} target="_blank" rel="noreferrer">
              {apps.oficina.label}
            </a>
          </li>
          <li>
            <strong>Bodega</strong> —{' '}
            <a href={apps.bodega.href} target="_blank" rel="noreferrer">
              {apps.bodega.label}
            </a>
          </li>
          <li>
            <strong>Tienda</strong> —{' '}
            <a href={apps.tienda.href} target="_blank" rel="noreferrer">
              {apps.tienda.label}
            </a>
          </li>
        </ul>
        <button type="button" className="link-btn" onClick={() => onNavigate?.('usuarios')}>
          Usuarios
        </button>
      </section>

      {data.sin_stock > 0 ? (
        <p className="dash-alert">
          ⚠ {data.sin_stock} productos marcados como agotados
          {' · '}
          <button type="button" className="link-btn" onClick={() => onNavigate?.('inventario')}>
            Ver inventario
          </button>
        </p>
      ) : null}
    </div>
  );
}
